import React from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import AvatarUploader from "@/components/avatar-upload";
import { useBanners } from "@/components/banner";
import Button from "@/components/button";
import FileUploader from "@/components/file-uploader";
import { Form } from "@/components/form";
import Input from "@/components/input";
import { PlacesAutocomplete } from "@/components/places-autocomplete";
import Textarea from "@/components/textarea";
import { useAuth } from "@/context/auth";
import { isApiError } from "@/queries/api/apiUtils";
import {
  useUploadAvatarMutation,
  useUploadResumeMutation,
} from "@/queries/hooks/fileUploadHooks";
import {
  useGetProfileQuery,
  useUpdateProfileMutation,
} from "@/queries/hooks/profileHooks";
import { notify } from "@/utils";
import { Logger } from "@/utils/logger";
import { MultiSelect } from "@/components/multi-select";
import { ROLE_OPTIONS as roles } from "@/constants/roles";
import { ChangePasswordDialog } from "./change-password-dialog";

type PersonalInfoValues = {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  address?: string;
  latitude?: number;
  longitude?: number;
  available_distance: number;
  about_me?: string;
  avatarFile?: File | null;
  preferred_roles: Array<string>;
};

// Matches the onboarding Location step's default and slider bounds.
const DEFAULT_RADIUS_MILES = 15;

export function PersonalInfoPanel() {
  const { t } = useTranslation("settings");
  const rolesOptions = roles;

  const PersonalInfoSchema = React.useMemo(
    () =>
      z.object({
        first_name: z.string().min(1, t("personal.errors.firstNameRequired")),
        last_name: z.string().min(1, t("personal.errors.lastNameRequired")),
        email: z.string().email(),
        phone: z.string().min(1),
        address: z.string().optional(),
        latitude: z.number().optional(),
        longitude: z.number().optional(),
        available_distance: z.number().min(2).max(50),
        about_me: z.string().max(500).optional(),
        avatarFile: z.instanceof(File).optional().nullable(),
        preferred_roles: z
          .array(z.string())
          .min(1, t("personal.errors.selectAtLeastOneRole"))
          .max(8),
      }),
    [t],
  );

  const auth = useAuth();
  const { data: profileData, isLoading: profileLoading, refetch: refetchProfile } = useGetProfileQuery();
  const updateProfileMutation = useUpdateProfileMutation();
  const uploadAvatarMutation = useUploadAvatarMutation();
  const uploadResumeMutation = useUploadResumeMutation();
  const { show } = useBanners();

  const profile = profileData?.profile;
  const resumeHref = profile?.resume_signed_url || profile?.resume_url || "";
  const resumeFileName = React.useMemo(() => {
    if (!resumeHref) return undefined;
    try {
      const path = new URL(resumeHref).pathname;
      const last = path.split("/").filter(Boolean).pop();
      if (!last) return undefined;
      const decoded = decodeURIComponent(last);
      const underscoreIdx = decoded.indexOf("_");
      return underscoreIdx > 0 && underscoreIdx < decoded.length - 1
        ? decoded.slice(underscoreIdx + 1)
        : decoded;
    } catch {
      return undefined;
    }
  }, [resumeHref]);

  // Debug logging
  React.useEffect(() => {
    console.log("Worker profile query state:", {
      isLoading: profileLoading,
      data: profileData,
      profile: profile,
      avatarUrl: profile?.avatar_url,
      avatar: profile?.avatar,
    });
  }, [profileLoading, profileData, profile]);

  const defaultValues: PersonalInfoValues = {
    first_name: auth.user?.firstName || "",
    last_name: auth.user?.lastName || "",
    email: auth.user?.email || "",
    phone: auth.user?.phone || "",
    address: profile?.address || "",
    latitude: profile?.latitude,
    longitude: profile?.longitude,
    available_distance: profile?.available_distance ?? DEFAULT_RADIUS_MILES,
    about_me: profile?.about_me || "",
    avatarFile: null,
    preferred_roles: profile?.preferred_roles || [],
  };

  const form = useForm<PersonalInfoValues>({
    resolver: zodResolver(PersonalInfoSchema),
    defaultValues,
    mode: "onBlur",
  });

  // Update form when data loads
  React.useEffect(() => {
    if (profile && auth.user) {
      const [first, ...lastParts] = (profile.full_name || "").split(" ");
      const last = lastParts.join(" ");
      form.reset({
        first_name: auth.user.firstName || first || "",
        last_name: auth.user.lastName || last || "",
        email: auth.user.email || "",
        phone: auth.user.phone || "",
        address: profile.address || "",
        latitude: profile.latitude,
        longitude: profile.longitude,
        available_distance: profile.available_distance ?? DEFAULT_RADIUS_MILES,
        about_me: profile.about_me || "",
        avatarFile: null,
        preferred_roles: profile.preferred_roles || [],
      });
    }
  }, [profile, auth.user, form]);

  const handleAvatarUpload = async (file: File | null) => {
    console.log("Worker handleAvatarUpload called with file:", file);
    if (!file) return;

    // Validate file type and size
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    const maxSize = 5 * 1024 * 1024; // 5MB — matches BE multipart cap and onboarding step

    if (!allowedTypes.includes(file.type)) {
      show({
        kind: "error",
        message: t("business.uploadJpegPngWebp"),
      });
      return;
    }

    if (file.size > maxSize) {
      show({ kind: "error", message: t("business.imageTooLarge") });
      return;
    }

    try {
      notify.info(t("business.uploadingLogo"));

      await uploadAvatarMutation.mutateAsync(file);

      show({ kind: "success", message: t("business.logoUploaded") });

      // Refetch profile to get updated avatar URL
      refetchProfile();
    } catch (error: unknown) {
      const isTooLarge = isApiError(error) && error.status === 413;
      show({
        kind: "error",
        message: isTooLarge
          ? t("personal.photoTooLargeServer")
          : error instanceof Error
            ? error.message
            : t("business.uploadLogoError"),
      });
      Logger.error("Avatar upload failed", {
        error: error instanceof Error ? error.message : String(error),
        status: isApiError(error) ? error.status : undefined,
      });
    }
  };

  const handleResumeUpload = async (file: File) => {
    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    const maxSize = 5 * 1024 * 1024;

    if (!allowedTypes.includes(file.type)) {
      show({ kind: "error", message: t("personal.resume.invalidType") });
      return;
    }
    if (file.size > maxSize) {
      show({ kind: "error", message: t("personal.resume.tooLarge") });
      return;
    }

    try {
      notify.info(t("personal.resume.uploading"));
      await uploadResumeMutation.mutateAsync(file);
      show({ kind: "success", message: t("personal.resume.uploaded") });
      refetchProfile();
    } catch (error: unknown) {
      const isTooLarge = isApiError(error) && error.status === 413;
      if (isTooLarge) {
        show({ kind: "error", message: t("personal.resume.tooLargeServer") });
        Logger.error("Resume upload failed", {
          error: error instanceof Error ? error.message : String(error),
          status: 413,
        });
        return;
      }
      show({
        kind: "error",
        message:
          error instanceof Error
            ? error.message
            : t("personal.resume.uploadError"),
      });
      Logger.error("Resume upload failed", {
        error: error instanceof Error ? error.message : String(error),
      });
    }
  };

  const onSubmit = async (values: PersonalInfoValues) => {
    try {
      const full_name = `${values.first_name} ${values.last_name}`.trim();
      const profileUpdate = {
        full_name,
        address: values.address,
        latitude: values.latitude,
        longitude: values.longitude,
        available_distance: values.available_distance,
        about_me: values.about_me,
        preferred_roles: values.preferred_roles,
      };

      await updateProfileMutation.mutateAsync(profileUpdate);

      // Update Auth context and localStorage with new name
      auth.updateUser({
        firstName: values.first_name,
        lastName: values.last_name,
      });

      notify.success(t("personal.saved"));
    } catch (error: unknown) {
      show({
        kind: "error",
        message:
          error instanceof Error
            ? error.message
            : t("personal.saveError"),
      });
      Logger.error("Profile update failed", {
        error: error instanceof Error ? error.message : String(error),
      });
    }
  };

  if (profileLoading) {
    return <div>{t("actions.loading", { ns: "common" })}</div>;
  }

  return (
    <div className="flex flex-col gap-6">
      <h3 className="text-xl font-semibold text-garnet mb-2">
        {t("personal.title")}
      </h3>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col gap-6"
        >
          <div className="w-full flex flex-col md:flex-row gap-5 md:gap-[20px] lg:gap-[90px]">
            {/* Avatar conditional rendering */}
            {profile?.avatar_url || profile?.avatar ? (
              <div
                className="relative group cursor-pointer"
                onClick={() => {
                  console.log("Worker existing avatar clicked");
                  document
                    .getElementById("settings-avatar-upload-input")
                    ?.click();
                }}
                tabIndex={0}
                role="button"
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    document
                      .getElementById("settings-avatar-upload-input")
                      ?.click();
                  }
                }}
              >
                <img
                  src={profile.avatar_url || profile.avatar}
                  alt={t("personal.title")}
                  className="rounded-full w-[128px] h-[128px] object-cover border border-garnet"
                />
                <input
                  id="settings-avatar-upload-input"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    console.log("Worker file input changed");
                    const file = e.target.files?.[0];
                    if (file) void handleAvatarUpload(file);
                  }}
                  disabled={updateProfileMutation.isPending}
                />
                {updateProfileMutation.isPending && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 rounded-full">
                    <span className="loader border-2 border-garnet border-t-transparent rounded-full w-10 h-10 animate-spin" />
                  </div>
                )}
                <div className="text-xs text-gray-500 mt-1 text-center w-full max-w-xs">
                  {t("personal.photoMaxSize")}
                </div>
              </div>
            ) : (
              <AvatarUploader
                size="lg"
                value={null}
                onChange={(file) => {
                  console.log(
                    "Worker AvatarUploader onChange called with file:",
                    file,
                  );
                  if (file) {
                    void handleAvatarUpload(file);
                  }
                }}
                maxSize={1 * 1024 * 1024}
                note={t("personal.photoMaxSize")}
              />
            )}

            <div className="flex flex-col w-full max-w-[400px] gap-4">
              <div className="grid grid-cols-2 gap-4">
                <Input
                  variantSize="sm"
                  label={t("personal.firstName")}
                  placeholder={t("personal.firstName")}
                  {...form.register("first_name")}
                  state={form.formState.errors.first_name ? "error" : "default"}
                  hint={form.formState.errors.first_name?.message}
                />
                <Input
                  variantSize="sm"
                  label={t("personal.lastName")}
                  placeholder={t("personal.lastName")}
                  {...form.register("last_name")}
                  state={form.formState.errors.last_name ? "error" : "default"}
                  hint={form.formState.errors.last_name?.message}
                />
              </div>

              <Input
                variantSize="sm"
                label={t("login.emailLabel", { ns: "auth" })}
                placeholder={t("login.emailLabel", { ns: "auth" })}
                {...form.register("email")}
                state={form.formState.errors.email ? "error" : "default"}
                hint={form.formState.errors.email?.message}
                disabled
              />

              <Input
                variantSize="sm"
                label={t("signup.phoneLabel", { ns: "auth" })}
                placeholder={t("signup.phoneLabel", { ns: "auth" })}
                {...form.register("phone")}
                state={form.formState.errors.phone ? "error" : "default"}
                hint={form.formState.errors.phone?.message}
                disabled
              />

              <PlacesAutocomplete
                label={t("personal.homeAddress")}
                value={form.watch("address") || ""}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  form.setValue("address", e.currentTarget.value)
                }
                onPlaceSelect={(place) => {
                  if (!place) {
                    form.setValue("address", "");
                    form.setValue("latitude", undefined);
                    form.setValue("longitude", undefined);
                    return;
                  }

                  const formatted = place.formattedAddress ?? place.name ?? "";
                  form.setValue("address", formatted);

                  const loc = place.location;
                  const lat =
                    typeof loc?.lat === "function" ? loc!.lat() : loc?.lat;
                  const lng =
                    typeof loc?.lng === "function" ? loc!.lng() : loc?.lng;
                  if (lat != null) form.setValue("latitude", Number(lat));
                  if (lng != null) form.setValue("longitude", Number(lng));
                }}
                state={form.formState.errors.address ? "error" : "default"}
                hint={form.formState.errors.address?.message}
              />

              <div className="flex flex-col gap-2">
                <label
                  htmlFor="settings-commute-radius"
                  className="body-small text-gray-700"
                >
                  {t("personal.commuteRadius", {
                    radius: form.watch("available_distance"),
                  })}
                </label>
                <input
                  id="settings-commute-radius"
                  type="range"
                  min={2}
                  max={50}
                  step={1}
                  value={form.watch("available_distance")}
                  onChange={(e) =>
                    form.setValue(
                      "available_distance",
                      Number(e.currentTarget.value),
                      { shouldDirty: true },
                    )
                  }
                  className="w-full accent-garnet"
                />
              </div>
            </div>
          </div>

          <Textarea
            label={t("personal.aboutYou")}
            {...form.register("about_me")}
            placeholder={t("personal.aboutYou")}
            state={form.formState.errors.about_me ? "error" : "default"}
            hint={form.formState.errors.about_me?.message}
          />

          <div>
            <MultiSelect
              label={t("personal.rolesPreferred")}
              value={form.watch("preferred_roles")}
              options={rolesOptions}
              onChange={(selected) =>
                form.setValue("preferred_roles", selected)
              }
              primaryFromFirst
            />
            {form.formState.errors.preferred_roles && (
              <span style={{ color: "red", fontSize: 12 }}>
                {form.formState.errors.preferred_roles.message}
              </span>
            )}
          </div>

          <div className="flex flex-col gap-3">
            <div className="flex items-baseline justify-between">
              <span className="font-semibold text-garnet">
                {t("personal.resume.title")}
              </span>
              <span className="body-small text-gray-500">
                {t("personal.resume.optional")}
              </span>
            </div>
            {resumeHref ? (
              <div className="flex items-center justify-between gap-3 rounded-md border border-garnet/30 bg-fog px-4 py-3">
                <div className="flex flex-col min-w-0">
                  <span className="body-small text-gray-500">
                    {t("personal.resume.currentResume")}
                  </span>
                  <a
                    href={resumeHref}
                    target="_blank"
                    rel="noreferrer"
                    className="font-semibold text-garnet underline truncate"
                    title={resumeFileName ?? t("personal.resume.view")}
                  >
                    {resumeFileName ?? t("personal.resume.view")}
                  </a>
                </div>
                <label className="shrink-0 cursor-pointer text-garnet underline body-small">
                  {uploadResumeMutation.isPending
                    ? t("personal.resume.uploading")
                    : t("personal.resume.replace")}
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    className="sr-only"
                    disabled={uploadResumeMutation.isPending}
                    onChange={(e) => {
                      const f = e.currentTarget.files?.[0];
                      if (f) void handleResumeUpload(f);
                      e.currentTarget.value = "";
                    }}
                  />
                </label>
              </div>
            ) : (
              <FileUploader
                label={t("personal.resume.uploadLabel")}
                accept={[".pdf", ".doc", ".docx"]}
                loading={uploadResumeMutation.isPending}
                disabled={uploadResumeMutation.isPending}
                note={t("personal.resume.maxSize")}
                onFileChange={(e) => {
                  const f = e.currentTarget.files?.[0];
                  if (f) void handleResumeUpload(f);
                  e.currentTarget.value = "";
                }}
              />
            )}
          </div>

          <ChangePasswordDialog />

          <div className="flex justify-end">
            <Button
              type="submit"
              variant="primary"
              disabled={updateProfileMutation.isPending}
            >
              {updateProfileMutation.isPending
                ? t("actions.saving", { ns: "common" })
                : t("actions.saveChanges", { ns: "common" })}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
